import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ClientCard, SearchView } from "../../../components";
import {
  deleteClient,
  downloadClientsWithNullLucaId,
  getClients,
  uploadClientsLucaInfo,
  uploadClientWithExcel,
  uploadCommissionInfoExcel,
} from "../../../data";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import ListView from "../../../components/list-view";
import SelectableListView from "../../../components/selectable-list-view";
import { useTableView } from "@/hooks/useTableView";
import { countryOptions } from "@/utils/country-options";
import { toast } from "@/components/ui/use-toast";

export function ClientListScreen() {
  const { isAdvancedTable } = useTableView();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const searchTimeoutRef = useRef(null);
  const [searchParams, setSearchParams] = useState({
    name: "",

    email: "",
    lucaId: "",
    country: "",
  });
  const [queryParams, setQueryParams] = useState({
    name: "",
    lucaId: "",
    email: "",
    country: "",
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isProcess, setIsProcess] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: [
      "clients",
      page,
      pageSize,
      queryParams,
      sortColumn,
      sortDirection,
    ],
    queryFn: () =>
      getClients({
        page,
        pageSize,
        nameFilter: queryParams.name,
        countryFilter: queryParams.country,
        lucaIdFilter: queryParams.lucaId,
        emailFilter: queryParams.email,
        sortColumn,
        sortDirection,
      }),
  });

  const fileSelect = useRef(null);
  const commissionFileSelect = useRef(null);
  const pageSettings = {
    title: "Client List",
    actions: [
      {
        name:
          "Download Null LucaId Clients (Count: " +
          data?.data?.lucaIdNullCount +
          ")",
        color: "bg-blue-500 hover:bg-blue-700 text-white",
        icon: "download-icon",
        onClick: () => {
          downloadClientsWithNullLucaId()
            .then((response) => {
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", "clients_null_lucaid.xlsx");
              document.body.appendChild(link);
              link.click();
              link.parentNode.removeChild(link);
            })
            .catch((error) => {
              const errorMessage =
                error.response?.status === 404
                  ? "All clients already have Luca ID"
                  : error.response?.message || "An unexpected error occurred";
              toast({
                title: "Information",
                description: errorMessage,
                variant: "destructive",
              });
              console.log(error);
            });
        },
      },
      {
        name: "Upload Luca Info",
        color: "bg-green-500 hover:bg-green-700 text-white",
        icon: "upload-icon",
        onClick: () => {
          const fileInput = document.createElement("input");
          fileInput.type = "file";
          fileInput.accept = ".xlsx,.xls";
          fileInput.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
              const formData = new FormData();
              formData.append("file", file);
              setIsProcess(true);
              uploadClientsLucaInfo(formData)
                .then((response) => {
                  setIsProcess(false);
                  queryClient.invalidateQueries(["clients"]);
                  const result = response.data;
                  toast({
                    title: "Luca Bilgisi Yüklendi",
                    description: result.message || `Güncellenen: ${result.updatedCount || 0}`,
                  });
                })
                .catch((error) => {
                  setIsProcess(false);
                  const errorData = error.response?.data;
                  
                  if (errorData?.error === "INVALID_HEADERS" || errorData?.error === "NO_UPDATE_FIELDS" || errorData?.error === "EMPTY_FILE") {
                    toast({
                      title: "Excel Başlık Hatası",
                      description: (
                        <div className="space-y-2">
                          <p>{errorData.message}</p>
                          {errorData.requiredHeaders && (
                            <p className="text-xs"><strong>Zorunlu:</strong> {errorData.requiredHeaders.join(", ")}</p>
                          )}
                          {errorData.expectedHeaders && (
                            <p className="text-xs"><strong>Beklenen:</strong> {errorData.expectedHeaders.join(", ")}</p>
                          )}
                          {errorData.receivedHeaders && (
                            <p className="text-xs"><strong>Gelen:</strong> {errorData.receivedHeaders.join(", ")}</p>
                          )}
                        </div>
                      ),
                      variant: "destructive",
                      duration: 10000,
                    });
                  } else {
                    toast({
                      title: "Hata",
                      description: errorData?.message || error.message || "Bir hata oluştu",
                      variant: "destructive",
                    });
                  }
                });
            }
          };
          fileInput.click();
        },
      },
      {
        name: "Excel Upload",
        color: "bg-red-100 hover:bg-red-400 hover:text-black text-red-800",
        icon: "add-icon",
        onClick: () => {
          fileSelect.current.click();
        },
      },
      {
        name: "Komisyon Bilgisi Yükle",
        color: "bg-purple-500 hover:bg-purple-700 text-white",
        icon: "upload-icon",
        onClick: () => {
          commissionFileSelect.current.click();
        },
      },
      {
        name: "Create New Client",
        color: "bg-blue-500 hover:bg-blue-700 text-white",
        icon: "add-icon",
        onClick: () => {
          navigate("/clients/add");
        },
      },
    ],
  };

  const goToEdit = (id) => () => {
    window.location.href = `/clients/${id}`;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setIsProcess(true);

    uploadClientWithExcel(formData)
      .then((response) => {
        setIsProcess(false);
        fileSelect.current.value = "";
        queryClient.invalidateQueries("clients");
        
        const result = response.data;
        toast({
          title: "Excel Yüklendi",
          description: result.message || `Güncellenen: ${result.updatedCount || 0}`,
        });
      })
      .catch((error) => {
        setIsProcess(false);
        fileSelect.current.value = "";
        
        const errorData = error.response?.data;
        
        if (errorData?.error === "INVALID_HEADERS" || errorData?.error === "NO_UPDATE_FIELDS" || errorData?.error === "EMPTY_FILE") {
          toast({
            title: "Excel Başlık Hatası",
            description: (
              <div className="space-y-2">
                <p>{errorData.message}</p>
                {errorData.requiredHeaders && (
                  <p className="text-xs"><strong>Zorunlu:</strong> {errorData.requiredHeaders.join(", ")}</p>
                )}
                {errorData.expectedHeaders && (
                  <p className="text-xs"><strong>Beklenen:</strong> {errorData.expectedHeaders.join(", ")}</p>
                )}
                {errorData.receivedHeaders && (
                  <p className="text-xs"><strong>Gelen:</strong> {errorData.receivedHeaders.join(", ")}</p>
                )}
              </div>
            ),
            variant: "destructive",
            duration: 10000,
          });
        } else {
          toast({
            title: "Hata",
            description: errorData?.message || error.message || "Bir hata oluştu",
            variant: "destructive",
          });
        }
      });
  };

  const handleCommissionFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setIsProcess(true);

    uploadCommissionInfoExcel(formData)
      .then((response) => {
        setIsProcess(false);
        commissionFileSelect.current.value = "";
        queryClient.invalidateQueries(["clients"]);
        
        const result = response.data;
        toast({
          title: "Komisyon Bilgisi Yüklendi",
          description: `Güncellenen: ${result.updatedClients}, Oluşturulan Ajans: ${result.createdAgencies}, Bağlanan Ajans: ${result.linkedAgencies}${result.clientsNotFound?.length > 0 ? `, Bulunamayan: ${result.clientsNotFound.length}` : ""}`,
        });
      })
      .catch((error) => {
        setIsProcess(false);
        commissionFileSelect.current.value = "";
        
        const errorData = error.response?.data;
        
        if (errorData?.error === "INVALID_HEADERS" || errorData?.error === "EMPTY_FILE") {
          toast({
            title: "Excel Başlık Hatası",
            description: (
              <div className="space-y-2">
                <p>{errorData.message}</p>
                {errorData.requiredHeaders && (
                  <p className="text-xs"><strong>Zorunlu:</strong> {errorData.requiredHeaders.join(", ")}</p>
                )}
                {errorData.expectedHeaders && (
                  <p className="text-xs"><strong>Beklenen:</strong> {errorData.expectedHeaders.join(", ")}</p>
                )}
                {errorData.receivedHeaders && (
                  <p className="text-xs"><strong>Gelen:</strong> {errorData.receivedHeaders.join(", ")}</p>
                )}
              </div>
            ),
            variant: "destructive",
            duration: 10000,
          });
        } else {
          toast({
            title: "Hata",
            description: errorData?.message || error.message || "Bir hata oluştu",
            variant: "destructive",
          });
        }
      });
  };

  const mutation = useMutation({
    mutationKey: ["deleteClient"],
    mutationFn: (data) => {
      return deleteClient(data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(["clients"]);
    },
  });

  const onDeleteClient = (id) => {
    console.log("delete client", id);
    mutation.mutate(id);
  };

  // Eski tablo için basit kolonlar
  const simpleColumns = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "country", label: "Country" },
    { key: "lucaAccountName", label: "Luca Account Name" },
    { key: "lucaId", label: "Luca ID" },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div>
          <Button onClick={goToEdit(row.id)}>Edit</Button>
        </div>
      ),
    },
  ];

  // Gelişmiş tablo için tüm kolonlar
  const advancedColumns = [
    { key: "id", label: "ID", sortable: true },
    { key: "name", label: "Name", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "phoneNumber", label: "Phone Number", sortable: true },
    { key: "manager", label: "Manager", sortable: true },
    { key: "billingName", label: "Billing Name", sortable: true },
    { key: "bankName", label: "Bank Name", sortable: true },
    { key: "IBAN", label: "IBAN", sortable: false },
    { key: "SWIFT", label: "SWIFT", sortable: false },
    { key: "lucaId", label: "Luca ID", sortable: true },
    { key: "lucaAccountName", label: "Luca Account Name", sortable: true },
    { key: "currency", label: "Currency", sortable: true },
    { key: "country", label: "Country", sortable: true },
    { key: "newTvAccountManager", label: "NewTV Manager", sortable: true },
    { 
      key: "newtvCommissionPercentage", 
      label: "Commission %", 
      sortable: true,
      accessor: (item) => item.newtvCommissionPercentage ? `${item.newtvCommissionPercentage}%` : "-"
    },
    { 
      key: "isPrepareTheInvoiceIndividually", 
      label: "Individual Invoice", 
      sortable: true,
      accessor: (item) => item.isPrepareTheInvoiceIndividually ? "Yes" : "No"
    },
    { 
      key: "isHaveAgency", 
      label: "Has Agency", 
      sortable: true,
      accessor: (item) => item.isHaveAgency ? "Yes" : "No"
    },
    { 
      key: "socialMediaAccounts", 
      label: "Social Media", 
      sortable: false,
      accessor: (item) => item.socialMediaAccounts?.map(acc => `${acc.platform}: ${acc.accountId}`).join(", ") || "-"
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      render: (row) => (
        <div>
          <Button size="sm" onClick={goToEdit(row.id)}>Edit</Button>
        </div>
      ),
    },
  ];

  const defaultVisibleColumns = ["name", "email", "country", "lucaAccountName", "lucaId", "actions"];

  const handleSearch = (item) => {
    const newSearchParams = { ...searchParams, [item.key]: item.value };
    setSearchParams(newSearchParams);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setQueryParams(newSearchParams);
      setPage(1);
    }, 300);
  };

  const handleSort = (column, sortDirection) => {
    setSortColumn(column);
    setSortDirection(sortDirection);
  };

  return (
    <AppLayout title={pageSettings.title} actions={pageSettings.actions}>
      <input
        ref={fileSelect}
        hidden
        type="file"
        onChange={handleFileChange}
        className="mb-4"
      />
      <input
        ref={commissionFileSelect}
        hidden
        type="file"
        accept=".xlsx,.xls"
        onChange={handleCommissionFileChange}
      />

      <SearchView
        searchParams={searchParams}
        handleSearch={handleSearch}
        countryOptions={countryOptions}
      />

      {isAdvancedTable ? (
        <SelectableListView
          isLoading={isLoading}
          data={data?.data?.clients}
          columns={advancedColumns}
          columnVisibilityKey="client-list-columns"
          defaultVisibleColumnKeys={defaultVisibleColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSort}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={10}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/clients/${item.id}`)}
        />
      ) : (
        <ListView
          isLoading={isLoading}
          handleSort={handleSort}
          data={data?.data?.clients}
          columns={simpleColumns}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={10}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/clients/${item.id}`)}
          renderGridItem={(item) => (
            <ClientCard
              name={item.name}
              email={item.email}
              phoneNumber={item.phoneNumber}
              country={item.country}
              manager={item.manager}
              billingName={item.billingName}
              bankName={item.bankName}
              IBAN={item.IBAN}
              SWIFT={item.SWIFT}
              lucaId={item.lucaId}
              lucaAccountName={item.lucaAccountName}
              currency={item.currency}
              newTvAccountManager={item.newTvAccountManager}
              isHaveAgency={item.isHaveAgency}
              socialMediaAccounts={item.socialMediaAccounts}
              editClick={goToEdit(item.id)}
            />
          )}
        />
      )}
    </AppLayout>
  );
}
